import { PermissionsKey } from '@vedalib/crud'
import { useCallback } from 'react'

import { BreadcrumbItem } from 'components/uiKit/Breadcrumbs/Breadcrumbs'
import { KitSize } from 'components/uiKit/KitTypes'
import Tabs, { TabPanel } from 'components/uiKit/Tabs'
import { EmployeePreferencesFinderTab, FileMetaFor } from 'gql/__generated__/graphql'
import { useCompanyGetById } from 'gql/companies/apollo'
import { usePermissions } from 'services/Permission/PermissionContext'
import { FinderItem } from 'store/models'
import { isFinderFile } from 'utils/useFinder'

import FinderPage from '../FinderPage'
import * as s from './FinderFilePicker.module.scss'
import { Unsplash } from './Unsplash'
import { useGetTabsOptions } from './hooks'
import { FinderTabsEnum, IFinderFilePickerProps } from './types'

const SHOW_FINDER_TABS: (EmployeePreferencesFinderTab | FinderTabsEnum)[] = [
  EmployeePreferencesFinderTab.companies,
  EmployeePreferencesFinderTab.projects,
]

const FinderFilePicker: React.FC<IFinderFilePickerProps> = ({
  paths,
  params,
  defaultContentType,
  tabs,
  checkbox = true,
  onFilePick,
}) => {
  const { data: companyData } = useCompanyGetById(params.companyId)
  const showExternalImages = companyData?.data?.showExternalImages
  const canAdd = usePermissions(PermissionsKey.project_resource_c)
  const { tabsOptions, activeTab, setActiveTab, activeFolder, setActiveFolder } = useGetTabsOptions(
    {
      projectId: params.projectId,
      contentType: defaultContentType,
      showExternalImages,
      tabs,
    },
  )

  const fileMetaGroupId = params.fileMetaGroupId || activeFolder || 'root'

  const onClickBreadcrumbs = useCallback(
    (breadcrumbItem: BreadcrumbItem) => setActiveFolder(breadcrumbItem.id || 'root'),
    [setActiveFolder],
  )
  const onClickItem = useCallback(
    (item: FinderItem) => {
      if (isFinderFile(item)) {
        onFilePick(item.id)
      } else {
        setActiveFolder(item.id)
      }
    },
    [onFilePick, setActiveFolder],
  )

  const handleResetFolder = useCallback(() => setActiveFolder('root'), [setActiveFolder])

  const handleSetActiveTab = useCallback(() => {
    const tab = params.projectId
      ? EmployeePreferencesFinderTab.projects
      : EmployeePreferencesFinderTab.companies
    const existTab = tabsOptions.find((t) => t.tabKey === tab)

    if (existTab) {
      setActiveTab(existTab.tabKey)
    }
  }, [params.projectId, setActiveTab, tabsOptions])

  return (
    <div className={s.root}>
      <Tabs
        activeKey={activeTab}
        defaultActiveKey={activeTab}
        name='tabs'
        onChange={setActiveTab}
        size={KitSize.S}
        styleType='clear'
        grow
      >
        {tabsOptions.map((tab) => (
          <TabPanel key={tab.tabKey} {...tab}>
            {SHOW_FINDER_TABS.includes(tab.tabKey) && (
              <FinderPage
                backCb={handleResetFolder}
                checkbox={checkbox}
                createdFor={
                  activeTab === EmployeePreferencesFinderTab.projects
                    ? FileMetaFor.projects
                    : FileMetaFor.companies
                }
                defaultContentType={defaultContentType}
                hiddenAddFile={!canAdd}
                onClickBreadcrumbs={onClickBreadcrumbs}
                onClickItem={onClickItem}
                params={{ ...params, fileMetaGroupId }}
                paths={paths}
                isModal
              />
            )}
            {tab.tabKey === FinderTabsEnum.unsplash && (
              <Unsplash
                checkbox={checkbox}
                defaultContentType={defaultContentType}
                handleSetActiveTab={handleSetActiveTab}
                onClose={onFilePick}
                params={params}
                parentId={fileMetaGroupId}
              />
            )}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  )
}

export default FinderFilePicker
